// gti-chms-frontend/src/components/MemberDashboardHome.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext'// Assuming AuthContext provides user data
import type { User } from '../../context/AuthContext';
/**
 * MemberDashboardHome component displays content specific to a regular church member'../..dashboard.
 * This includes a welcome message, latest church announcements, and a snapshot of their profile.
 * It is designed to be rendered within the <Outlet /> of the DashboardPage.
 */
const DashboardHome: React.FC = () => {
    // Access the authenticated user object from the AuthContext
    const { user } = useAuth();

    // Mock data for church announcements.
    // In a production application, this data would be fetched from a backend API
    // (e.g., from a Firestore collection named '../..nouncements'../..
    const announcements = [
        {
            id: 1,
            title: 'coming Prayer Meeting',
            date: 'june 28, 2025',
            content: 'join us for our weekly prayer meeting this Friday at 7 PM in the main sanctuary.',
        },
        {
            id: 2,
            title: 'youth Group Summer Camp',
            date: 'july 15, 2025',
            content: 'registration is now open for the annual Youth Group Summer Camp! Limited spots available.',
        },
        {
            id: 3,
            title: 'community Outreach Event',
            date: 'august 5, 2025',
            content: 'are organizing a community clean-up drive. Volunteers needed! Sign up on the events page.',
        },
    ];

    // Display a loading message if user data is not yet available.
    // This is a defensive check, as ProtectedRoute should typically ensure user is loaded.
    if (!user) {
        return <div className="p-8 text-center text-gray-600">Loading your dashboard content...</div>;
    }

    return (
        <div className="p-6 space-y-6">
            {/* Welcome Message Card */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-3xl font-bold text-gray-800 mb-2">Welcome, {user.firstName}!</h2>
                <p className="text-gray-600">We'../.. glad to have you here. Here'../..a quick overview of what'../..happening in our community.</p>
            </div>

            {/* Grid for Announcements and Profile Snapshot */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Latest Church Announcements Card */}
                <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4">Latest Church Announcements</h3>
                    {announcements.length > 0 ? (
                        <div className="space-y-4">
                            {announcements.map((announcement) => (
                                <div key={announcement.id} className="border-b border-gray-200 pb-3 last:border-b-0">
                                    <h4 className="text-lg font-medium text-blue-700">{announcement.title}</h4>
                                    <p className="text-sm text-gray-500 mb-1">{announcement.date}</p>
                                    <p className="text-gray-700 text-sm">{announcement.content}</p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-600">No new announcements at the moment. Check back soon!</p>
                    )}
                </div>

                {/* My Profile Snapshot Card */}
                <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4">My Profile Snapshot</h3>
                    <div className="space-y-2 text-gray-700">
                        <p><strong>Name:</strong> {user.firstName} {user.lastName}</p>
                        <p><strong>Email:</strong> {user.email}</p>
                        {/* Conditionally display phone number if available in the user object */}
                        {user?.phoneNumber && <p><strong>Phone:</strong> {user?.phoneNumber}</p>}
                        {/* Conditionally display address if available in the user object */}
                        {user?.address && <p><strong>Address:</strong> {user?.address}</p>}
                        {/* Add other relevant profile fields as they become available in your user object */}
                    </div>
                    <div className="mt-6">
                        {/* Link to the user'../..profile editing page */}
                        <Link
                            to="/dashboard/profile" // Assuming '../..ashboard/profile'../..s the route for editing a user'../..profile
                            className="inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 shadow-sm"
                        >
                            Edit My Profile
                        </Link>
                    </div>
                </div>
            </div>

            {/* Additional member-specific widgets can be added here in the future,
                e.g., a summary of personal contributions, upcoming events they'../.. registered for. */}
        </div>
    );
};

export default DashboardHome;

